import { USMapsBoolean } from './boolean';
import { MapState } from './state';

/**
 * Represents a location marker on the map.
 *
 * You can make as many location markers as you like, but each location must have a unique id ('0', '1', '2', etc).
 *
 * Reference: https://simplemaps.com/docs/locations
 */
export class MapLocation {
  /**
   * The name of the location.
   */
  name?: string;
  /**
   * The latitude of the location in decimal form.
   */
  lat: number | string = 0;
  /**
   * The longitude of the location in decimal form.
   */
  lng: number | string = 0;
  /**
   * The location's color. If it exists, it will overwrite main_settings.location_color.
   */
  color?: string;
  /**
   * The location's color upon hover. If it exists, it will overwrite main_settings.location_hover_color.
   */
  hover_color?: string;
  /**
   * This will fill up the popup and can include HTML/CSS. If it exists, it will overwrite main_settings.location_description.
   */
  description?: string;
  /**
   * The url when the location is clicked. (e.g. 'http://simplemaps.com'). If it exists, it will overwrite main_settings.location_url.
   */
  url?: string;
  /**
   * 'yes' makes the location invisible.
   */
  hide?: USMapsBoolean;
  /**
   * 'yes' will make hovering or clicking on a location have no effect. If it exists, it will overwrite main_settings.all_states_inactive.
   */
  inactive?: USMapsBoolean;
  /**
   * The size of the location. If it exists, it will overwrite main_settings.location_size.
   */
  size?: number;
  /**
   * The shape of the location. If it exists, it will overwrite main_settings.location_type. ['image', 'circle', 'square', 'marker', 'triangle', 'heart', 'star', 'diamond']
   */
  type?: 'image' | 'circle' | 'square' | 'marker' | 'triangle' | 'heart' | 'star' | 'diamond';
  /**
   * If type is set to 'image', this is the url where the image resides [e.g. 'https://simplemaps.com/images/frog.png'].
   */
  image_url?: string;
  /**
   * If type is set to 'image', this is the url where the image shown on hover resides [e.g. 'https://simplemaps.com/images/frog.png'].
   */
  image_hover_url?: string;
  /**
   * The opacity of the location. If it exists, it will overwrite main_settings.location_opacity. [0-1]
   */
  opacity?: number;
  /**
   * The opacity of the location upon hover. If it exists, it will overwrite main_settings.location_hover_opacity. [0-1]
   */
  hover_opacity?: number;
  /**
   * The location's border color. If it exists, it will overwrite main_settings.location_border_color.
   */
  border_color?: string;
  /**
   * Size of location border. (e.g. 2). If it exists, it will overwrite main_settings.location_border.
   */
  border?: number;
  /**
   * Size of location border upon hover. (e.g. 2.5). If it exists, it will overwrite main_settings.location_hover_border.
   */
  hover_border?: number;
  /**
   * If 'yes' the location's size will remain constant as the map zooms. 'yes' by default.
   */
  scale?: USMapsBoolean;
  /**
   * Determines when the location will be visible. Options are: 'all', 'out', 'region', 'state'. Use of a zoom ratio [0-1], is also permitted. In the case of '.25', a location would be visible when the map had zoomed in such that the viewbox width was less than 25% of the initial view's width.
   */
  display?: string;
  /**
   * 'yes' will make the location pulse on hover. 'no' by default.
   */
  pulse?: USMapsBoolean;
  /**
   * The magnitude of the pulse. '4' by default.
   */
  pulse_size?: number;
  /**
   * The speed of the pulse in seconds. '.5' by default.
   */
  pulse_speed?: number;
  /**
   * The color of the pulse. The location color by default.
   */
  pulse_color?: string;

  constructor(location: MapLocation) {
    Object.assign(this, location);
  }
}

/**
 * A zoom location on the map detailing the Cost of Care state and city
 */
export interface ZoomLocation extends Partial<MapLocation>, Partial<MapState> {}
